import { useCallback, useEffect, useState } from "react";
import api, { errorMessage } from "../lib/api";
import { toast } from "sonner";
import { PageHeader, ActionButton, GhostButton, PillBadge, FormField, inputClass } from "../components/Primitives";
import { Plus, Trash2, Pencil, Building2, Users, Save } from "lucide-react";

const empty = {
  name: "", street_address: "", city: "", state: "", zip_code: "",
  phone: "", email: "", is_active: true,
};

export default function Offices() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/offices");
      setRows(res.data || []);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const startNew = () => { setEditing(null); setForm(empty); setOpenForm(true); };
  const startEdit = (row) => { setEditing(row); setForm({ ...empty, ...row }); setOpenForm(true); };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Office name is required"); return; }
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/offices/${editing.id}`, form);
        toast.success("Office updated");
      } else {
        await api.post("/offices", form);
        toast.success("Office added");
      }
      setOpenForm(false);
      setEditing(null);
      setForm(empty);
      await load();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete office "${row.name}"?`)) return;
    try {
      await api.delete(`/offices/${row.id}`);
      toast.success("Office deleted");
      await load();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="offices-page">
      <PageHeader
        title="Agency Offices"
        subtitle="Physical branches your  report to. Officers can be assigned to an office on their profile — used for payroll grouping and roster filtering."
        action={<ActionButton onClick={startNew} data-testid="office-new-btn"><Plus className="size-3.5" /> New office</ActionButton>}
        testId="offices-header"
      />

      {loading ? (
        <div className="p-10 text-center text-zinc-500">Loading offices…</div>
      ) : rows.length === 0 ? (
        <div className="border border-[#27272A] bg-[#0A0A0A] p-10 text-center" data-testid="offices-empty">
          <Building2 className="size-8 mx-auto text-zinc-600 mb-2" />
          <div className="text-zinc-300">No offices yet</div>
          <div className="text-xs text-zinc-500 mt-1">Add your first branch so you can assign  to it.</div>
        </div>
      ) : (
        <div className="border border-[#27272A] bg-[#0A0A0A]">
          <table className="w-full text-sm">
            <thead className="bg-[#121212] text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">City</th>
                <th className="text-left px-4 py-3">Phone</th>
                <th className="text-left px-4 py-3">Officers</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1c1c1e]">
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-[#101014]" data-testid={`office-row-${r.id}`}>
                  <td className="px-4 py-3 text-zinc-100 font-medium">{r.name}</td>
                  <td className="px-4 py-3 text-zinc-400">{r.city || "—"}{r.state ? `, ${r.state}` : ""}</td>
                  <td className="px-4 py-3 text-zinc-400 font-mono text-xs">{r.phone || "—"}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 text-zinc-300">
                      <Users className="size-3.5" /> {r.guard_count || 0}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <PillBadge tone={r.is_active ? "green" : "muted"}>{r.is_active ? "active" : "inactive"}</PillBadge>
                  </td>
                  <td className="px-4 py-3 flex justify-end gap-2">
                    <GhostButton onClick={() => startEdit(r)} data-testid={`office-edit-${r.id}`}><Pencil className="size-3.5" /> Edit</GhostButton>
                    <GhostButton onClick={() => remove(r)} data-testid={`office-delete-${r.id}`}><Trash2 className="size-3.5" /> Delete</GhostButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openForm && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6" onClick={() => setOpenForm(false)}>
          <div className="bg-[#0F0F12] border border-[#27272A] w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-testid="office-form-modal">
            <div className="label-mute mb-4">{editing ? "Edit office" : "New office"}</div>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Name *"><input className={inputClass} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="office-form-name" /></FormField>
              <FormField label="Phone"><input className={inputClass} value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} data-testid="office-form-phone" /></FormField>
              <FormField label="Street address" className="col-span-2"><input className={inputClass} value={form.street_address || ""} onChange={(e) => set("street_address", e.target.value)} data-testid="office-form-street" /></FormField>
              <FormField label="City"><input className={inputClass} value={form.city || ""} onChange={(e) => set("city", e.target.value)} data-testid="office-form-city" /></FormField>
              <FormField label="State"><input className={inputClass} value={form.state || ""} onChange={(e) => set("state", e.target.value)} data-testid="office-form-state" /></FormField>
              <FormField label="Zip code"><input className={inputClass} value={form.zip_code || ""} onChange={(e) => set("zip_code", e.target.value)} data-testid="office-form-zip" /></FormField>
              <FormField label="Email"><input className={inputClass} value={form.email || ""} onChange={(e) => set("email", e.target.value)} data-testid="office-form-email" /></FormField>
              <label className="col-span-2 flex items-center gap-2 cursor-pointer select-none">
                <input type="checkbox" className="accent-[#FF3B30]" checked={!!form.is_active} onChange={(e) => set("is_active", e.target.checked)} data-testid="office-form-active" />
                <span className="text-xs uppercase tracking-widest text-zinc-400">Active</span>
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <GhostButton onClick={() => setOpenForm(false)}>Cancel</GhostButton>
              <ActionButton onClick={submit} disabled={busy} data-testid="office-form-save"><Save className="size-3.5" /> {busy ? "Saving…" : "Save"}</ActionButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
